import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, Calendar, Trophy, BarChart, MapPin, Bell, Shield, Settings,
  ChevronRight, ArrowRight, Play, Check, Star, Globe, Smartphone,
  Zap, Award, Target, Clock, CreditCard, MessageSquare
} from 'lucide-react';
import MegaMenu from '../../components/public/MegaMenu';
import Footer from '../../components/public/Footer';

// Platform Features
const FEATURES = [
  {
    icon: Users,
    title: 'Member Management',
    description: 'Complete golfer profiles, handicap tracking, membership tiers, and renewal management.',
    color: 'from-green-600 to-green-700'
  },
  {
    icon: Calendar,
    title: 'Tournament Management',
    description: 'Create and manage tournaments with automated scoring, leaderboards, and bracket generation.',
    color: 'from-blue-600 to-blue-700'
  },
  {
    icon: Trophy,
    title: 'Handicap System',
    description: 'Official handicap calculation following World Handicap System standards.',
    color: 'from-yellow-600 to-yellow-700'
  },
  {
    icon: BarChart,
    title: 'Analytics Dashboard',
    description: 'Real-time insights on player performance, course statistics, and membership trends.',
    color: 'from-purple-600 to-purple-700'
  },
  {
    icon: MapPin,
    title: 'Course Management',
    description: 'Tee time bookings, course conditions, hole-by-hole data, and facility management.',
    color: 'from-red-600 to-red-700'
  },
  {
    icon: CreditCard,
    title: 'Payment Integration',
    description: 'Seamless payment processing for memberships, green fees, and tournament entries.',
    color: 'from-cyan-600 to-cyan-700'
  }
];

// Platform Modules
const MODULES = [
  { name: 'Member Portal', description: 'Self-service member registration and profile management' },
  { name: 'Tournament Engine', description: 'Automated tournament creation and management' },
  { name: 'Tee Time Booking', description: 'Online booking system with availability calendar' },
  { name: 'Handicap Calculator', description: 'WHS-compliant handicap calculation and tracking' },
  { name: 'Leaderboard System', description: 'Real-time scoring and live leaderboards' },
  { name: 'Communication Hub', description: 'Push notifications, SMS, and email alerts' },
  { name: 'Reporting Suite', description: 'Comprehensive analytics and custom reports' },
  { name: 'Mobile App', description: 'iOS and Android apps for golfers on the go' }
];

// Testimonials
const TESTIMONIALS = [
  {
    quote: "MyGolfHub transformed how we manage our club. Tournament registrations that used to take days now happen in minutes.",
    author: "James Mwangi",
    role: "Club Captain",
    organization: "Karen Country Club",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop"
  },
  {
    quote: "The handicap system is incredibly accurate and our members love being able to track their progress on their phones.",
    author: "Sarah Ochieng",
    role: "Golf Operations Manager",
    organization: "Muthaiga Golf Club",
    image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop"
  },
  {
    quote: "We've seen a 40% increase in tournament participation since implementing MyGolfHub. The user experience is exceptional.",
    author: "Peter Kamau",
    role: "Tournament Director",
    organization: "Kenya Golf Union",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop"
  }
];

// Stats
const STATS = [
  { value: '50+', label: 'Golf Clubs' },
  { value: '15,000+', label: 'Active Golfers' },
  { value: '500+', label: 'Tournaments Managed' },
  { value: '8', label: 'African Countries' }
];

const MyGolfHubPage = () => {
  const [activeModule, setActiveModule] = useState(0);

  return (
    <div className="min-h-screen bg-black">
      <MegaMenu />
      
      {/* Hero Section */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#008000]/20 via-black to-black" />
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#008000]/20 border border-[#008000]/30 rounded-full text-[#00CC00] text-sm font-medium mb-6">
                <Zap size={16} />
                Sports Technology
              </span>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6">
                MyGolfHub
                <span className="block text-2xl sm:text-3xl lg:text-4xl text-white/60 mt-2">
                  Africa's Premier Golf Management Platform
                </span>
              </h1>
              
              <p className="text-white/70 text-lg mb-8 leading-relaxed">
                The complete digital solution for golf clubs, unions, and tournaments across Africa. 
                Manage members, track handicaps, run tournaments, and grow your golf community with 
                a platform built specifically for African golf.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link
                  to="/contact"
                  data-testid="mygolfhub-demo-btn"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#008000] hover:bg-[#006600] text-white font-semibold rounded-lg transition-all shadow-lg shadow-[#008000]/25"
                >
                  Request Demo
                  <ArrowRight size={18} />
                </Link>
                <a
                  href="#features"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-all border border-white/20"
                >
                  <Play size={18} />
                  See How It Works
                </a>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {STATS.map((stat) => (
                  <div key={stat.label} className="text-left">
                    <p className="text-2xl font-heading font-bold text-[#008000]">{stat.value}</p>
                    <p className="text-white/50 text-sm">{stat.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
            
            {/* Platform Preview */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                <img 
                  src="https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=800&h=500&fit=crop"
                  alt="MyGolfHub Dashboard"
                  className="w-full"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                
                {/* Floating UI Elements */}
                <div className="absolute top-4 right-4 bg-white/10 backdrop-blur-md rounded-lg p-3 border border-white/20">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-white text-sm font-medium">Live Leaderboard</span>
                  </div>
                </div>
                
                <div className="absolute bottom-4 left-4 bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/20">
                  <p className="text-[#00CC00] font-heading font-bold text-xl">15,234</p>
                  <p className="text-white/70 text-sm">Active Members</p>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#008000]/20 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#008000]/10 rounded-full blur-3xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#008000] font-medium text-sm uppercase tracking-wider">Platform Features</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mt-4 mb-6">
              Everything You Need to Manage Golf
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              A comprehensive suite of tools designed specifically for the unique needs of African golf clubs and federations.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, idx) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-[#008000]/50 hover:bg-white/10 transition-all"
              >
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-heading font-bold text-lg mb-2">{feature.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#008000] font-medium text-sm uppercase tracking-wider">Platform Modules</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mt-4 mb-6">
                Modular Design for Your Needs
              </h2>
              <p className="text-white/60 mb-8">
                Choose the modules that fit your organization. Scale up as you grow.
              </p>
              
              <div className="space-y-3">
                {MODULES.map((module, idx) => (
                  <button
                    key={module.name}
                    onClick={() => setActiveModule(idx)}
                    className={`w-full text-left p-4 rounded-lg transition-all ${
                      activeModule === idx 
                        ? 'bg-[#008000]/20 border border-[#008000]/50' 
                        : 'bg-white/5 border border-white/10 hover:border-white/20'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className={`font-medium ${activeModule === idx ? 'text-[#00CC00]' : 'text-white'}`}>
                          {module.name}
                        </h4>
                        <p className="text-white/50 text-sm mt-1">{module.description}</p>
                      </div>
                      <ChevronRight className={`w-5 h-5 transition-transform ${
                        activeModule === idx ? 'text-[#008000] rotate-90' : 'text-white/30'
                      }`} />
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#008000]/20 to-transparent border border-white/10 p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-[#008000] flex items-center justify-center">
                    <Globe className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-heading font-bold">{MODULES[activeModule].name}</h3>
                    <p className="text-white/50 text-sm">{MODULES[activeModule].description}</p>
                  </div>
                </div>
                
                <img 
                  src="https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=600&h=400&fit=crop"
                  alt={MODULES[activeModule].name}
                  className="w-full rounded-lg"
                />
                
                <div className="mt-6 grid grid-cols-2 gap-4">
                  <div className="bg-white/5 rounded-lg p-4">
                    <Smartphone className="w-6 h-6 text-[#008000] mb-2" />
                    <p className="text-white text-sm font-medium">Mobile Ready</p>
                    <p className="text-white/50 text-xs">iOS & Android</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <Shield className="w-6 h-6 text-[#008000] mb-2" />
                    <p className="text-white text-sm font-medium">Secure</p>
                    <p className="text-white/50 text-xs">Enterprise grade</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#008000] font-medium text-sm uppercase tracking-wider">Testimonials</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mt-4">
              Trusted by Leading Golf Organizations
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((testimonial, idx) => (
              <motion.div
                key={testimonial.author}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-xl p-6"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#008000] text-[#008000]" />
                  ))}
                </div>
                <p className="text-white/80 mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.author}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-white font-medium">{testimonial.author}</p>
                    <p className="text-white/50 text-sm">{testimonial.role}</p>
                    <p className="text-[#008000] text-sm">{testimonial.organization}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#008000] to-[#006600] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
              Ready to Transform Your Golf Operations?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              Join 50+ golf clubs across Africa already using MyGolfHub to manage their operations efficiently.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#008000] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                Schedule Demo
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors"
              >
                Contact Sales
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MyGolfHubPage;
